// src/pages/Signup.jsx
import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { registerFirm, checkFirmName, verifySignupCode, resendSignupCode, getFeatureFlags, getPublicPricing } from '../api/kailux'
import { positivePrice, nonNegativeNumber, formatRand } from '../utils/pricing'
import { supabase } from '../supabase'
import { Sparkles, FileText, CreditCard, MapPin } from 'lucide-react'
import ErrorMessage from '../components/ErrorMessage'
import TurnstileWidget from '../components/TurnstileWidget'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const SEAT_PRICE = 500 // R/user/month (fallback only: see DEFAULT_PRICES; mirrors backend/pricing_config.py)
const PAY_PER_ENGAGEMENT_AFS_PRICE = 295  // R/AFS — mirrors backend/pricing_config.py PAY_PER_ENGAGEMENT_AFS_PRICE
const PAY_PER_ENGAGEMENT_MGMT_PRICE = 150 // R/file — mirrors backend/pricing_config.py PAY_PER_ENGAGEMENT_MGMT_PRICE
const AFS_CREDITS_PER_SEAT = 10 // AFS/user/month pooled (mirrors backend/pricing_config.py AFS_CREDITS_PER_SEAT)

// These five constants are only the FALLBACK. Signup is shown before anyone has
// a session, so it can't read the admin-editable platform_config; it asks the
// public GET /api/public/pricing instead and shows what is actually in force
// (the page promises "you'll only ever be charged the exact amount above", so
// it must not quote a stale number). If that request fails these are shown.
// backend/tests/test_public_pricing.py fails if any of them drifts from
// pricing_config.py.
const DEFAULT_PRICES = {
  seat: SEAT_PRICE, credits: AFS_CREDITS_PER_SEAT,
  afs: PAY_PER_ENGAGEMENT_AFS_PRICE, mgmt: PAY_PER_ENGAGEMENT_MGMT_PRICE,
}

// Aug 2026 (kailux-fixes-log.md §17): Review/Audit used to always render
// here with a disabled, greyed-out "COMING SOON" badge — every unbuilt
// feature showing up as visible clutter on the very first screen a new
// firm sees. Now each entry's `flag` key is checked against the live
// feature_flags config (see the ENGAGEMENTS filter below) and the option
// simply doesn't render at all while its flag is off — same underlying
// gate (backend enforces the flag too, see register_firm), just a much
// cleaner signup experience until the real workflow behind each one ships.
const ENGAGEMENTS = [
  { id:'compilation', label:'Compilation Engagement', sub:'ISRS 4410 — most common' },
  { id:'review',      label:'Independent Review',     sub:'ISRE 2400', flag:'engagement_type_review_enabled' },
  { id:'audit',       label:'Full Audit',             sub:'ISA Standards', flag:'engagement_type_audit_enabled' },
  // No flag — real report/letter content exists for this from the start,
  // unlike review/audit above. This is just the FIRM-LEVEL default; the
  // actual restriction to CC clients is enforced per-client on the Clients
  // page and again server-side, since entity_type isn't known at signup.
  { id:'accounting_officer', label:"Accounting Officer's Report", sub:'Close Corporations Act — for CC clients' },
  // No flag. For a company preparing its OWN financial statements through an
  // internal preparer: no external accountant, so the AFS carries a Statement
  // by the Preparer instead of a compilation/review/audit report.
  { id:'in_house', label:'Prepared in-house', sub:'My own company, prepared by my own staff. Without an audit or review this is only lawful if owner-managed with a Public Interest Score below 100' },
]

export default function Signup() {
  const [step,          setStep]          = useState(1)
  const [loading,       setLoading]       = useState(false)
  const [error,         setError]         = useState('')
  const [firmExists,    setFirmExists]    = useState(false)
  const [firmExistName, setFirmExistName] = useState('')
  const [checkingFirm,  setCheckingFirm]  = useState(false)
  const [flags,         setFlags]         = useState({})
  const [form,          setForm]          = useState({
    firmName:'', email:'', password:'', confirm:'',
    userCount:1, engagement:'compilation', planType:'subscription',
    practiceNumber:'', individualMemberNumber:'', regNumber:'', regulatoryBody:'SAICA',
  })

  useEffect(() => { getFeatureFlags().then(setFlags).catch(() => setFlags({})) }, [])

  const [prices, setPrices] = useState(DEFAULT_PRICES)
  useEffect(() => {
    let cancelled = false
    // Best effort: on any failure (network, 5xx, malformed body) keep the
    // built-in prices and say nothing -- a visitor shouldn't see an error about
    // a number they didn't ask for. Each field is validated on its own, the
    // same way the backend validates what it charges.
    Promise.resolve()
      .then(() => getPublicPricing())
      .then((p) => {
        if (cancelled || !p || typeof p !== 'object') return
        setPrices({
          seat:    positivePrice(p.seat_price, SEAT_PRICE),
          credits: nonNegativeNumber(p.afs_credits_per_seat, AFS_CREDITS_PER_SEAT),
          afs:     positivePrice(p.pay_per_engagement_afs_price, PAY_PER_ENGAGEMENT_AFS_PRICE),
          mgmt:    positivePrice(p.pay_per_engagement_mgmt_price, PAY_PER_ENGAGEMENT_MGMT_PRICE),
        })
      })
      .catch(() => { /* keep DEFAULT_PRICES */ })
    return () => { cancelled = true }
  }, [])
  const visibleEngagements = ENGAGEMENTS.filter(e => !e.flag || flags[e.flag])

  const navigate = useNavigate()
  const set = (k,v) => setForm(f=>({...f,[k]:v}))

  const handleFirmNameBlur = async () => {
    const name = form.firmName.trim()
    if (name.length < 3) { setFirmExists(false); return }
    setCheckingFirm(true)
    try {
      const { data } = await checkFirmName(name)
      if (data?.exists) {
        setFirmExists(true)
        setFirmExistName(data.name || name)
      } else {
        setFirmExists(false)
        setFirmExistName('')
      }
    } catch { /* non-fatal — don't block signup */ }
    finally { setCheckingFirm(false) }
  }

  const validate = () => {
    if (!form.firmName.trim()) { setError('Firm name is required'); return false }
    if (!form.email.includes('@')) { setError('Valid email required'); return false }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return false }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return false }
    if (!form.practiceNumber.trim() && !form.individualMemberNumber.trim() && !form.regNumber.trim()) {
      setError("Please provide at least one of: Practice Number, Individual Member Number, "
        + "or Firm Registration Number. You don't need all three — just one — and you can "
        + "add the others later from Settings.")
      return false
    }
    setError(''); return true
  }

  const handleNext = () => {
    if (step === 1 && !validate()) return
    setStep(s=>s+1)
  }

  const [turnstileToken, setTurnstileToken] = useState('')
  const [pendingFirmId, setPendingFirmId] = useState(null)
  const [pendingPnStatus, setPendingPnStatus] = useState(null)
  const [verifyCode, setVerifyCode] = useState('')
  const [verifyError, setVerifyError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendMsg, setResendMsg] = useState('')

  const handleSubmit = async () => {
    setLoading(true); setError('')
    try {
      const { data } = await registerFirm({
        firmName:       form.firmName,
        email:          form.email,
        password:       form.password,
        userCount:      form.userCount,
        engagement:     form.engagement,
        planType:       form.planType,
        practiceNumber: form.practiceNumber,
        individualMemberNumber: form.individualMemberNumber,
        regNumber:      form.regNumber,
        regulatoryBody: form.regulatoryBody,
        turnstileToken,
      })
      // Aug 2026 — see kailux-fixes-log.md §12. Signing in and navigating
      // no longer happens immediately here — the account now requires
      // email verification first (a code, not a magic link, per direct
      // product decision). Step 3 below handles entering it; only on
      // success does handleVerifyCode sign in and navigate, using the
      // same practice_number_status branch that used to live here.
      setPendingFirmId(data.firm_id)
      setPendingPnStatus(data.practice_number_status)
      setStep(3)
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create account. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    setVerifying(true); setVerifyError('')
    try {
      await verifySignupCode(pendingFirmId, verifyCode.trim())
      await supabase.auth.signInWithPassword({
        email: form.email, password: form.password
      })
      if (pendingPnStatus === 'pending_verification') {
        navigate('/practice-verification')
      } else {
        // Free trial removed (Sep 2026): every new firm starts 'unpaid' —
        // fully usable except AFS generation. Send them to Billing first so
        // they can subscribe right away or explicitly choose to skip for
        // now (see Billing.jsx's isPostSignup / "Skip for now" handling),
        // rather than silently dropping them into Onboarding unpaid with no
        // prompt either way.
        navigate('/billing?postSignup=1')
      }
    } catch (err) {
      setVerifyError(err.response?.data?.detail || 'Incorrect code. Please try again.')
      setVerifying(false)
    }
  }

  const handleResendCode = async () => {
    setResending(true); setResendMsg(''); setVerifyError('')
    try {
      await resendSignupCode(pendingFirmId)
      setResendMsg('A new code is on its way — check your inbox.')
    } catch (err) {
      setVerifyError(err.response?.data?.detail || 'Could not resend the code. Please try again shortly.')
    } finally {
      setResending(false)
    }
  }

  const inp = (label, key, type='text', ph='', extraProps={}) => (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
        color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
        {label.toUpperCase()}
      </label>
      <input type={type} value={form[key]} onChange={e=>set(key,e.target.value)}
        placeholder={ph} style={{ width:'100%', padding:'12px 14px',
          background:T.s3, border:`1px solid ${T.bd}`, borderRadius:'8px',
          color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
          outline:'none', boxSizing:'border-box' }}
        onFocus={e=>e.target.style.borderColor=T.or}
        onBlur={e=>{e.target.style.borderColor=T.bd; extraProps.onBlur?.()}}
        {...Object.fromEntries(Object.entries(extraProps).filter(([k])=>k!=='onBlur'))}
      />
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.tx,
      fontFamily:"'Outfit',sans-serif", display:'flex' }}>

      {/* Left panel */}
      <div style={{ width:'360px', flexShrink:0, background:T.s1,
        borderRight:`1px solid ${T.bd}`, padding:'48px 36px',
        display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'48px' }}>
          <div style={{ width:'36px', height:'36px', borderRadius:'8px',
            background:T.prg,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontFamily:"'Syne',sans-serif", fontWeight:'800', color:T.onpri, fontSize:'16px' }}>K</div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
        </div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'800',
          letterSpacing:'-1px', lineHeight:'1.1', marginBottom:'12px' }}>
          Get your<br/><span style={{color:T.or}}>AFS platform</span><br/>set up today.
        </div>
        <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'36px' }}>
          Create your account free. Subscribe whenever you're ready to generate AFS.
        </div>
        {[
          [Sparkles,'AI maps everything','Accounts and transactions — automatic'],
          [FileText,'Complete IFRS AFS','23 mandatory notes, all five statements'],
          [MapPin,'Built for SA','PAYE, UIF, SDL, CIPC — natively supported'],
          [CreditCard,'Cancel anytime','No lock-in, no setup fees'],
        ].map(([icon,title,desc],i)=>(
          <div key={i} style={{ display:'flex', gap:'12px', marginBottom:'16px' }}>
            {typeof icon === 'string'
              ? <span style={{ fontSize:'20px', flexShrink:0 }}>{icon}</span>
              : (() => { const Icon = icon; return <Icon size={20} strokeWidth={1.75} style={{ flexShrink:0 }} color={T.or} /> })()}
            <div>
              <div style={{ fontSize:'14px', fontWeight:'600', marginBottom:'2px' }}>{title}</div>
              <div style={{ fontSize:'12px', color:T.mt }}>{desc}</div>
            </div>
          </div>
        ))}
        <div style={{ marginTop:'auto', fontSize:'13px', color:T.mt }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color:T.or, fontWeight:'700', textDecoration:'none' }}>
            Sign in
          </Link>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ flex:1, display:'flex', alignItems:'center',
        justifyContent:'center', padding:'48px 40px', overflowY:'auto' }}>
        <div style={{ width:'100%', maxWidth:'500px' }}>

          {/* Steps */}
          <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'32px' }}>
            {['Account','Plan','Done'].map((s,i)=>(
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <div style={{ width:'28px', height:'28px', borderRadius:'50%',
                  background:i+1<step?T.gn:i+1===step?T.or:T.s2,
                  border:`1.5px solid ${i+1<step?T.gn:i+1===step?T.or:T.bd}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'12px', fontWeight:'800',
                  color:i+1<=step?T.onpri:T.mt, transition:'all 0.3s' }}>
                  {i+1<step?<Icon name="check" />:i+1}
                </div>
                <span style={{ fontSize:'13px', fontWeight:'600',
                  color:i+1===step?T.tx:T.mt }}>{s}</span>
                {i<2 && <div style={{ width:'32px', height:'1px',
                  background:i+1<step?T.gn:T.bd }}/>}
              </div>
            ))}
          </div>

          {error && <ErrorMessage style={{ marginBottom:'16px' }}>{error}</ErrorMessage>}

          {/* Step 1 — Account */}
          {step===1 && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
                fontWeight:'800', marginBottom:'6px' }}>Create your account</div>
              <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>
                Your firm's login details
              </div>

              {/* Firm name field with duplicate check */}
              <div style={{ marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  ACCOUNTING FIRM NAME
                </label>
                <div style={{ position:'relative' }}>
                  <input type="text" value={form.firmName}
                    onChange={e=>{ set('firmName',e.target.value); setFirmExists(false) }}
                    onBlur={handleFirmNameBlur}
                    placeholder="e.g. Ndlovu & Associates Inc."
                    style={{ width:'100%', padding:'12px 14px',
                      background:T.s3, border:`1px solid ${firmExists ? T.yl : T.bd}`,
                      borderRadius:'8px', color:T.tx, fontSize:'14px',
                      fontFamily:"'Outfit',sans-serif", outline:'none', boxSizing:'border-box' }}
                    onFocus={e=>e.target.style.borderColor=firmExists?T.yl:T.or}
                  />
                  {checkingFirm && (
                    <span style={{ position:'absolute', right:'12px', top:'50%',
                      transform:'translateY(-50%)', fontSize:'12px', color:T.mt }}>
                      Checking…
                    </span>
                  )}
                </div>

                {/* Duplicate firm nudge */}
                {firmExists && (
                  <div style={{ marginTop:'10px', padding:'12px 14px',
                    background:alpha(T.am, 0.08),
                    border:`1px solid ${alpha(T.am, 0.35)}`, borderRadius:'8px',
                    display:'flex', gap:'8px', alignItems:'flex-start' }}>
                    <span style={{ fontSize:'16px', flexShrink:0 }}><Icon name="alert" /></span>
                    <div>
                      <div style={{ fontSize:'13px', fontWeight:'700',
                        color:T.yl, marginBottom:'3px' }}>
                        "{firmExistName}" is already on KaiLux
                      </div>
                      <div style={{ fontSize:'12px', color:T.am, lineHeight:'1.6' }}>
                        If you work at this firm, ask your administrator to add you as a
                        team member rather than creating a separate account.
                        Duplicate accounts cannot share clients or billing.
                      </div>
                      <button
                        onClick={() => { setFirmExists(false); setFirmExistName('') }}
                        style={{ marginTop:'8px', fontSize:'12px', fontWeight:'700',
                          color:T.yl, background:'none', border:'none',
                          cursor:'pointer', padding:0, textDecoration:'underline' }}>
                        This is a different firm — continue anyway
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {inp('Work Email','email','email','you@yourfirm.co.za')}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                {inp('Password','password','password','Min 8 characters')}
                {inp('Confirm Password','confirm','password','Repeat password')}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px' }}>
                {inp('Practice Number','practiceNumber','text','e.g. 12345678')}
                <div style={{ marginBottom:'14px' }}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                    BODY
                  </label>
                  <select value={form.regulatoryBody}
                    onChange={e=>set('regulatoryBody', e.target.value)}
                    style={{ width:'100%', padding:'12px 14px',
                      background:T.s3, border:`1px solid ${T.bd}`, borderRadius:'8px',
                      color:T.tx, fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                      outline:'none', boxSizing:'border-box' }}>
                    <option value="SAICA">SAICA</option>
                    <option value="SAIPA">SAIPA</option>
                    <option value="IRBA">IRBA</option>
                    {/* CIPC-recognised bodies (Aug 2026) — previously missing,
                        so a CIBA/IAC/CIMA/ACCA/ICSA/SAIGA member (e.g. an
                        accounting officer) had no accurate option and had to
                        pick "Other". */}
                    <option value="CIBA">CIBA / SAIBA</option>
                    <option value="IAC">IAC</option>
                    <option value="CIMA">CIMA</option>
                    <option value="ACCA">ACCA</option>
                    <option value="CGISA">CGISA (ICSA)</option>
                    <option value="SAIGA">SAIGA</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'-8px', marginBottom:'14px' }}>
                Your firm's registered practice number.
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                {inp('Individual Member Number','individualMemberNumber','text','e.g. SAICA00123456')}
                {inp('Firm Registration Number','regNumber','text','e.g. 2021/445821/07')}
              </div>
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'-8px', marginBottom:'14px' }}>
                <strong style={{ color:T.tx }}>We need at least one of these three</strong> — you
                don't need all of them, and you can add or change the others later from Settings.
                If you're signing in your own individual capacity (e.g. a SAICA/SAIPA sole
                practitioner without a firm-level practice number), your personal member number
                goes here instead — it'll appear on your AFS in place of a practice number.
                Firm Registration Number is your CIPC number — leave it blank if you're not tied
                to a registered company or CC.
              </div>
            </div>
          )}

          {/* Step 2 — Team size */}
          {step===2 && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
                fontWeight:'800', marginBottom:'6px' }}>How would you like to pay?</div>
              <div style={{ fontSize:'14px', color:T.mt, marginBottom:'20px' }}>
                Choose whichever fits your engagement volume — you can see both, no charge today.
              </div>

              <div style={{ display:'flex', gap:'12px', marginBottom:'22px' }}>
                <button type="button" onClick={()=>set('planType','subscription')}
                  style={{ flex:1, textAlign:'left', padding:'16px', borderRadius:'12px', cursor:'pointer',
                    border: form.planType!=='pay_per_engagement' ? `2px solid ${T.tx}` : `1.5px solid ${T.bd}`,
                    background:T.s2 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:'800', fontSize:'14px' }}>Monthly subscription</div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                    {formatRand(prices.seat)}/user/month — AFS credits pooled and included
                  </div>
                </button>
                <button type="button" onClick={()=>set('planType','pay_per_engagement')}
                  style={{ flex:1, textAlign:'left', padding:'16px', borderRadius:'12px', cursor:'pointer',
                    border: form.planType==='pay_per_engagement' ? `2px solid ${T.tx}` : `1.5px solid ${T.bd}`,
                    background:T.s2 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:'800', fontSize:'14px' }}>Pay-per-engagement</div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px' }}>
                    No monthly fee — {formatRand(prices.afs)} per AFS, {formatRand(prices.mgmt)} per management accounts file
                  </div>
                </button>
              </div>

              {form.planType === 'pay_per_engagement' ? (
                <div style={{ background:T.s2, border:`1.5px solid ${T.bd}`, borderRadius:'12px',
                  padding:'20px', marginBottom:'20px', fontSize:'13px', color:T.mt }}>
                  A card is required on file before you can generate an AFS or management accounts
                  file on this plan (added right after signup, from Billing) — you'll only ever be
                  charged the exact amount above, at the moment you generate each one. No monthly
                  bill, nothing to cancel.
                </div>
              ) : (
              <>
              <div style={{ fontSize:'14px', color:T.mt, marginBottom:'20px' }}>
                One plan, every feature, {formatRand(prices.seat)}/user/month. No charge today —
                you can subscribe from Billing right after signup, or later.
                You can add or remove seats any time from Billing.
              </div>

              <div style={{ background:T.s2, border:`1.5px solid ${T.bd}`,
                borderRadius:'12px', padding:'20px', marginBottom:'20px',
                display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:'12px', color:T.mt, marginBottom:'4px' }}>Team size</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'15px', fontWeight:'800' }}>
                    {formatRand(prices.seat * form.userCount)}/month
                  </div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                    {prices.credits * form.userCount} AFS/month pooled across the firm
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                  <button type="button" onClick={()=>set('userCount', Math.max(1, form.userCount-1))} disabled={form.userCount<=1}
                    style={{ width:'36px', height:'36px', borderRadius:'10px', border:`1px solid ${T.bd}`,
                      background:T.s1, color:T.tx, fontSize:'18px', fontWeight:'700',
                      cursor: form.userCount<=1 ? 'not-allowed' : 'pointer', opacity: form.userCount<=1 ? 0.4 : 1 }}>−</button>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'900',
                    minWidth:'36px', textAlign:'center' }}>{form.userCount}</div>
                  <button type="button" onClick={()=>set('userCount', form.userCount+1)}
                    style={{ width:'36px', height:'36px', borderRadius:'10px', border:`1px solid ${T.bd}`,
                      background:T.s1, color:T.tx, fontSize:'18px', fontWeight:'700', cursor:'pointer' }}>+</button>
                </div>
              </div>
              </>
              )}

              <div style={{ marginBottom:'4px' }}>
                <div style={{ fontSize:'10px', color:T.lt, fontWeight:'700',
                  letterSpacing:'1px', marginBottom:'10px' }}>DEFAULT ENGAGEMENT TYPE</div>
                {visibleEngagements.map(e=>(
                  <label key={e.id} style={{ display:'flex', gap:'8px', alignItems:'center',
                    padding:'10px 12px', borderRadius:'8px', cursor:'pointer',
                    background:form.engagement===e.id?T.ord:T.s2,
                    border:`1px solid ${form.engagement===e.id?T.or:T.bd}`,
                    marginBottom:'8px', transition:'all 0.2s' }}>
                    <input type="radio" name="eng" value={e.id}
                      checked={form.engagement===e.id}
                      onChange={()=>set('engagement',e.id)}
                      style={{ accentColor:T.or }}/>
                    <div>
                      <div style={{ fontSize:'14px', fontWeight:'600',
                        color:form.engagement===e.id?T.or:T.tx, display:'flex', alignItems:'center', gap:'6px' }}>
                        {e.label}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt }}>{e.sub}</div>
                    </div>
                  </label>
                ))}
              </div>

              <TurnstileWidget onVerify={setTurnstileToken} />
            </div>
          )}

          {/* Step 3 — Email verification (Aug 2026, kailux-fixes-log.md §12) */}
          {step===3 && (
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
                fontWeight:'800', marginBottom:'6px' }}>Check your email</div>
              <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>
                We've sent a 6-digit code to <strong style={{ color:T.tx }}>{form.email}</strong>.
                Enter it below to finish setting up your account.
              </div>

              {verifyError && <ErrorMessage style={{ marginBottom:'16px' }}>{verifyError}</ErrorMessage>}

              <div style={{ marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  VERIFICATION CODE
                </label>
                <input type="text" inputMode="numeric" maxLength={6} value={verifyCode}
                  onChange={e=>setVerifyCode(e.target.value.replace(/\D/g,''))}
                  placeholder="123456" autoFocus
                  style={{ width:'100%', padding:'14px', background:T.s3,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'22px', fontWeight:'700', letterSpacing:'0.3em',
                    textAlign:'center', fontFamily:'monospace',
                    outline:'none', boxSizing:'border-box' }}/>
              </div>

              <div style={{ fontSize:'13px', color:T.mt }}>
                Didn't get it?{' '}
                <button type="button" onClick={handleResendCode} disabled={resending}
                  style={{ background:'none', border:'none', color:T.or,
                    fontSize:'13px', fontFamily:"'Outfit',sans-serif",
                    cursor: resending ? 'not-allowed' : 'pointer', padding:0,
                    textDecoration:'underline' }}>
                  {resending ? 'Sending…' : 'Resend code'}
                </button>
              </div>
              {resendMsg && <div style={{ fontSize:'13px', color:T.gn, marginTop:'8px' }}>{resendMsg}</div>}
            </div>
          )}

          {/* Navigation */}
          <div style={{ display:'flex', justifyContent:'space-between',
            alignItems:'center', marginTop:'24px' }}>
            {step>1 && step<3 ? (
              <button onClick={()=>setStep(s=>s-1)}
                style={{ background:'none', border:'none', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
                ← Back
              </button>
            ) : <div/>}
            <button
              onClick={step===3 ? handleVerifyCode : step===2 ? handleSubmit : handleNext}
              disabled={loading || verifying || (step===3 && verifyCode.trim().length !== 6) ||
                (step===2 && !!import.meta.env.VITE_TURNSTILE_SITE_KEY && !turnstileToken)}
              style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
                background:(loading||verifying)?T.mt:T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif",
                fontSize:'14px', fontWeight:'700',
                cursor:(loading||verifying)?'not-allowed':(step===3 && verifyCode.trim().length !== 6)?'not-allowed':'pointer',
                opacity: (step===3 && verifyCode.trim().length !== 6 && !verifying) ? 0.5 : 1,
                display:'flex', alignItems:'center', gap:'8px' }}>
              {(loading || verifying) ? (
                <>
                  <span style={{ width:'14px', height:'14px',
                    border:`2px solid ${alpha(T.onpri, 0.3)}`,
                    borderTopColor:T.onpri, borderRadius:'50%',
                    animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                  {step===3 ? 'Verifying...' : 'Creating account...'}
                </>
              ) : step===3 ? 'Verify →' : step===2 ? 'Create Account →' : 'Continue →'}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
